import {
  createTheme,
  type BreakpointsOptions,
  type Palette,
  type Theme,
  type ThemeOptions,
  type TypographyVariantsOptions,
} from "@mui/material";
import type { ThemeList } from "api";
import { removeUndefined } from "utils";

export type ThemeMode = "light" | "dark";

export type StaticThemeOptions = {
  typography?:
    | TypographyVariantsOptions
    | ((palette: Palette) => TypographyVariantsOptions)
    | undefined;
  breakpoints?: BreakpointsOptions | undefined;
};

const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

export function createThemeFromConfig(
  config?: ThemeList | undefined,
  parentConfig?: ThemeList,
): {
  light: Theme;
  dark: Theme;
} {
  const staticOptions: StaticThemeOptions = {
    typography: {
      // fontFamily: `'Poppins', sans-serif`,
      fontFamily: `'Roboto', sans-serif`,
      // fontFamily: `'Open Sans', sans-serif`,

      h1: {
        fontSize: "44px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "40px" },
      },
      h2: {
        fontSize: "40px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "36px" },
      },
      h3: {
        fontSize: "36px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "32px" },
      },
      h4: {
        fontSize: "32px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "28px" },
      },
      h5: {
        fontSize: "28px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "24px" },
      },
      h6: {
        fontSize: "24px",
        fontWeight: 400,
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "20px" },
      },
      body1: {
        fontSize: "20px",
        // [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "16px" },
      },
      body2: {
        fontSize: "20px",
        // [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "16px" },
      },
      label: {
        fontSize: "16px",
        [`@media (max-width:${breakpoints.md - 1}px)`]: { fontSize: "16px" },
        fontWeight: 300,
      },
      smallLabel: {
        fontSize: "12px",
        [`@media (max-width:${breakpoints.md - 1}px)`]: {
          fontSize: "12px !important",
        },
        fontWeight: 300,
      },
    },
    breakpoints: { values: breakpoints },
  };

  const light: ThemeOptions = {
    typography: staticOptions?.typography,
    breakpoints: staticOptions?.breakpoints,
    palette: {
      mode: "light",
      primary:
        config?.light?.palette?.usePrimary && config.light?.palette?.primary
          ? {
              main: config.light.palette.primary,
            }
          : parentConfig?.light?.palette?.usePrimary &&
              parentConfig.light?.palette?.primary
            ? {
                main: parentConfig.light.palette.primary,
              }
            : undefined,
      secondary:
        config?.light?.palette?.useSecondary &&
        config?.light?.palette?.secondary
          ? {
              main: config.light.palette.secondary,
            }
          : parentConfig?.light?.palette?.useSecondary &&
              parentConfig.light?.palette?.secondary
            ? {
                main: parentConfig.light.palette.secondary,
              }
            : undefined,
      error:
        config?.light?.palette?.useError && config.light?.palette?.error
          ? {
              main: config.light.palette.error,
            }
          : parentConfig?.light?.palette?.useError &&
              parentConfig.light?.palette?.error
            ? {
                main: parentConfig.light.palette.error,
              }
            : undefined,
      warning:
        config?.light?.palette?.useWarning && config.light?.palette?.warning
          ? {
              main: config.light.palette.warning,
            }
          : parentConfig?.light?.palette?.useWarning &&
              parentConfig.light?.palette?.warning
            ? {
                main: parentConfig.light.palette.warning,
              }
            : undefined,
      info:
        config?.light?.palette?.useInfo && config.light?.palette?.info
          ? {
              main: config.light.palette.info,
            }
          : parentConfig?.light?.palette?.useInfo &&
              parentConfig.light?.palette?.info
            ? {
                main: parentConfig.light.palette.info,
              }
            : undefined,
      success:
        config?.light?.palette?.useSuccess && config.light?.palette?.success
          ? {
              main: config.light.palette.success,
            }
          : parentConfig?.light?.palette?.useSuccess &&
              parentConfig.light?.palette?.success
            ? {
                main: parentConfig.light.palette.success,
              }
            : undefined,
      background: {
        paper:
          config?.light?.background?.usePaper && config.light.background.paper
            ? config.light.background.paper
            : parentConfig?.light?.background?.usePaper &&
                parentConfig.light?.background?.paper
              ? parentConfig.light.background.paper
              : undefined,
        default:
          config?.light?.background?.useBackground &&
          config.light.background.background
            ? config.light.background.background
            : parentConfig?.light?.background?.useBackground &&
                parentConfig.light?.background?.background
              ? parentConfig.light.background.background
              : undefined,
      },
    },
  };

  const dark: ThemeOptions = {
    typography: staticOptions?.typography,
    breakpoints: staticOptions?.breakpoints,
    palette: {
      mode: "dark",
      primary:
        config?.dark?.palette?.usePrimary && config.dark?.palette?.primary
          ? {
              main: config.dark.palette.primary,
            }
          : parentConfig?.dark?.palette?.usePrimary &&
              parentConfig.dark?.palette?.primary
            ? {
                main: parentConfig.dark.palette.primary,
              }
            : undefined,
      secondary:
        config?.dark?.palette?.useSecondary && config.dark?.palette?.secondary
          ? {
              main: config.dark.palette.secondary,
            }
          : parentConfig?.dark?.palette?.useSecondary &&
              parentConfig.dark?.palette?.secondary
            ? {
                main: parentConfig.dark.palette.secondary,
              }
            : undefined,
      error:
        config?.dark?.palette?.useError && config.dark?.palette?.error
          ? {
              main: config.dark.palette.error,
            }
          : parentConfig?.dark?.palette?.useError &&
              parentConfig.dark?.palette?.error
            ? {
                main: parentConfig.dark.palette.error,
              }
            : undefined,
      warning:
        config?.dark?.palette?.useWarning && config.dark?.palette?.warning
          ? {
              main: config.dark.palette.warning,
            }
          : parentConfig?.dark?.palette?.useWarning &&
              parentConfig.dark?.palette?.warning
            ? {
                main: parentConfig.dark.palette.warning,
              }
            : undefined,
      info:
        config?.dark?.palette?.useInfo && config.dark?.palette?.info
          ? {
              main: config.dark.palette.info,
            }
          : parentConfig?.dark?.palette?.useInfo &&
              parentConfig.dark?.palette?.info
            ? {
                main: parentConfig.dark.palette.info,
              }
            : undefined,
      success:
        config?.dark?.palette?.useSuccess && config.dark?.palette?.success
          ? {
              main: config.dark.palette.success,
            }
          : parentConfig?.dark?.palette?.useSuccess &&
              parentConfig.dark?.palette?.success
            ? {
                main: parentConfig.dark.palette.success,
              }
            : undefined,
      background: {
        paper:
          config?.dark?.background?.usePaper && config.dark.background.paper
            ? config.dark.background.paper
            : parentConfig?.dark?.background?.usePaper &&
                parentConfig.dark?.background?.paper
              ? parentConfig.dark.background.paper
              : undefined,
        default:
          config?.dark?.background?.useBackground &&
          config.dark.background.background
            ? config.dark.background.background
            : parentConfig?.dark?.background?.useBackground &&
                parentConfig.dark?.background?.background
              ? parentConfig.dark.background.background
              : undefined,
      },
    },
  };

  return {
    light: createTheme(removeUndefined(light)),
    dark: createTheme(removeUndefined(dark)),
  };
}
